import { useMemo } from 'react'
import { isNonNullable, uniqueBy } from 'lib/array/arrayUtils'

interface ColorOption {
  key: string
  colors: Array<string>
  isSelected: boolean
  /** Only present for dual-color options */
  variant?: App.MerchandiseOfferVariant
}

interface ColorLabel {
  typeName: string
  selectedName: string
}

interface ColorOptionData {
  /** Whether there are 2+ color types that should be combined */
  shouldCombine: boolean
  /** Color options for display (works for single and dual color) */
  options: Array<ColorOption>
  /** Labels showing selected color names (only for dual-color) */
  colorLabels: Array<ColorLabel>
}

const EMPTY_RESULT: ColorOptionData = {
  shouldCombine: false,
  options: [],
  colorLabels: [],
}

/**
 * Hook to extract color options from merchandise variants.
 * - Single color type: returns unique color swatches
 * - Dual color types: returns combined half-and-half swatches
 */
function useMerchandiseColorOptions(
  variants: Array<App.MerchandiseOfferVariant> | undefined,
  selectedVariant?: App.MerchandiseOfferVariant,
): ColorOptionData {
  return useMemo(() => {
    if (!variants?.length) return EMPTY_RESULT

    // Find color option positions from template variant
    const colorPositions = variants[0].options
      ?.map((opt, i) => ({ position: i, typeName: opt.type, isColor: opt.displayType === 'colour' }))
      .filter(p => p.isColor) ?? []

    if (colorPositions.length === 0) return EMPTY_RESULT

    // Single color type
    if (colorPositions.length === 1) {
      const position = colorPositions[0].position
      const selectedName = selectedVariant?.options?.[position]?.name

      const colorOptions = variants
        .map(v => v.options?.[position])
        .filter((opt): opt is App.MerchandiseOfferColourOption => !!opt && 'colour' in opt && !!opt.colour)

      const uniqueColors = uniqueBy(colorOptions, opt => opt.colour)

      return {
        shouldCombine: false,
        options: uniqueColors.map(opt => ({
          key: opt.id,
          colors: [opt.colour!],
          isSelected: opt.name === selectedName,
        })),
        colorLabels: [],
      }
    }

    // Dual color types
    const [first, second] = colorPositions
    const selectedName1 = selectedVariant?.options?.[first.position]?.name
    const selectedName2 = selectedVariant?.options?.[second.position]?.name

    const combinations = variants
      .map(v => {
        const opt1 = v.options?.[first.position]
        const opt2 = v.options?.[second.position]
        if (!opt1 || !opt2 || !('colour' in opt1) || !('colour' in opt2)) return null

        const color1 = opt1.colour
        const color2 = opt2.colour
        if (!color1 || !color2) return null

        return {
          key: `${opt1.name}|${opt2.name}`,
          colors: [color1, color2] as [string, string],
          name1: opt1.name,
          name2: opt2.name,
          variant: v,
        }
      })
      .filter(isNonNullable)

    const uniqueCombos = uniqueBy(combinations, c => c.key)

    return {
      shouldCombine: true,
      options: uniqueCombos.map(c => ({
        key: c.key,
        colors: c.colors,
        isSelected: c.name1 === selectedName1 && c.name2 === selectedName2,
        variant: c.variant,
      })),
      colorLabels: selectedVariant ? [
        { typeName: first.typeName, selectedName: selectedName1 ?? '' },
        { typeName: second.typeName, selectedName: selectedName2 ?? '' },
      ].filter(l => l.selectedName) : [],
    }
  }, [variants, selectedVariant])
}

export default useMerchandiseColorOptions
